import { FC, SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { Jane } from 'types/graphql';
import {
  AvatarIconWrapper,
  JaneSelect,
  ListItemWrapperForGroups,
  OverlayAvatarImage,
} from 'pages/Settings/modals/GroupsModal/components/SearchTeamMembersGroups/SearchTeamMembersGroups.styled';
import { Flex } from 'components/Flex';
import { StyledBKJIcon } from 'components/JaneGridSelect/JaneGridSelect.styled';
import { BKJIcon, BKJCheckbox, BKJAvatar } from '@bookjane2/bookjane-design-library';
import { loadedAvatarCache } from 'pages/Settings/modals/GroupsModal/components/SearchTeamMembersGroups/SearchTeamMembersGroups.constants';

interface ITeamMemberOption {
  node: Jane;
  name: string;
  isChecked: boolean;
  onSelect: (e: unknown) => void;
  showGroupIcon?: boolean;
}

const AVATAR_SIZE = '30px';

const OptionAvatar: FC<{ url: string; pendingSignup?: boolean }> = ({ url, pendingSignup }) => {
  const [isLoaded, setIsLoaded] = useState<boolean>(!!url && (loadedAvatarCache.get(url) ?? false));

  useEffect(() => {
    if (!url) {
      setIsLoaded(false);
      return;
    }

    const fromCache = loadedAvatarCache.get(url);
    if (fromCache) {
      setIsLoaded(true);
      return;
    }

    const img = new Image();
    img.onload = () => {
      loadedAvatarCache.set(url, true);
      setIsLoaded(true);
    };
    img.onerror = () => {
      loadedAvatarCache.set(url, false);
      setIsLoaded(false);
    };
    img.src = url;
  }, [url]);

  return (
    <AvatarIconWrapper>
      <BKJAvatar width={AVATAR_SIZE} height={AVATAR_SIZE} fallbackAvatarColor="Grey4" />
      {isLoaded && url ? <OverlayAvatarImage src={url} alt="avatar" /> : null}
      {pendingSignup && <StyledBKJIcon iconName="PendingSignup" left="17" top="16" width="20" />}
    </AvatarIconWrapper>
  );
};

const TeamMemberOptionComponent: FC<ITeamMemberOption> = ({
  node,
  isChecked,
  name,
  onSelect,
  showGroupIcon,
}) => {
  const fullName = `${node?.firstName} ${node?.lastName}`;

  const handleClick = useCallback(
    (event: SyntheticEvent) => {
      event.preventDefault && event.preventDefault();
      onSelect({
        ...event,
        target: {
          ...event.target,
          node: { ...node },
          isChecked,
          name,
        },
      });
    },
    [isChecked, name, node, onSelect],
  );

  return (
    <JaneSelect key={node.id} alignItems="center" selected={false}>
      <ListItemWrapperForGroups
        variant="RowReverseSpaceBetween"
        role="listbox"
        onClick={handleClick}
        checked={isChecked}
      >
        <Flex width="100%" flexDirection="row" alignItems="center" gap="8px;">
          <BKJCheckbox variant="GreenSolid" value={isChecked} />
          <OptionAvatar url={node?.avatarUrls?.small || ''} pendingSignup={node?.pendingSignup} />
          <span>{fullName}</span>
          {showGroupIcon && <BKJIcon iconName="Group2" width={16} height={14} />}
        </Flex>
      </ListItemWrapperForGroups>
    </JaneSelect>
  );
};

export const TeamMemberOption = TeamMemberOptionComponent;

TeamMemberOption.displayName = 'TeamMemberOption';
